import {createTranslator, type useTranslations} from "next-intl"
import english from "@/translations/en/plan-recurrence.json"
import type { BillingPlan } from '@/lib/billing/types'

const englishTranslator = createTranslator({locale: 'en', messages: english, namespace: 'PlanRecurrence'})
type RecurrenceTranslator = ReturnType<typeof useTranslations<'PlanRecurrence'>>

/**
 * Pure billing-recurrence label helpers for the Plan card. Kept out of the page
 * component so they can be unit-tested without pulling the page's client/server
 * dependencies (server actions, supabase).
 */

function getPlanRecurringAmountCents(
  plan: BillingPlan,
  interval: string,
  count: number,
): number {
  const matched = plan.billing_intervals?.find(
    (i) => i.interval === interval && i.interval_count === count,
  )
  if (matched?.stripe_price_amount_cents != null) return matched.stripe_price_amount_cents
  // Fallback: naive monthly × cycle length. Misses plan-level discount but
  // only reached when stripe_price_amount_cents hasn't been cached yet.
  const monthsInCycle = interval === 'month' ? count : interval === 'year' ? count * 12 : count
  return plan.price_monthly_cents * monthsInCycle
}

function formatIntervalSuffix(interval: string, count: number, t: RecurrenceTranslator): string {
  switch (interval) {
    case "month":
      if (count === 1) return t("month")
      if (count === 3) return t("quarter")
      return t("months", {count})
    case "year": return count === 1 ? t("year") : t("years", {count})
    default: return t("cycle")
  }
}

/**
 * Secondary line under the plan name on the Plan card — surfaces the recurring
 * billing info ("$30/mo · next charge May 28"). Returns null when there's nothing
 * useful to say (e.g. a Platform-managed plan with no period_end on file).
 *
 * `timezone` (viewer IANA tz, default 'UTC') is threaded explicitly so the helper
 * stays pure and boundary timestamps don't silently shift a day.
 */
export function renderPlanRecurrenceLine(
  subscription: {
    payment_provider: string
    billing_interval: string
    billing_interval_count: number
    current_period_end: string | null
    cancel_at_period_end: boolean
    pending_plan_change_initiated_at: string | null
  },
  plan: BillingPlan,
  timezone: string = 'UTC',
  t: RecurrenceTranslator = englishTranslator,
  locale = 'en',
): string | null {
  const dateLabel = formatPeriodDate(subscription.current_period_end, timezone, locale)

  if (subscription.pending_plan_change_initiated_at) {
    return t("pending")
  }
  if (subscription.cancel_at_period_end) {
    return dateLabel ? t("cancelsOn", {date: dateLabel}) : t("cancelsEnd")
  }
  if (subscription.payment_provider === 'manual') {
    return dateLabel ? t("renews", {date: dateLabel}) : null
  }
  if (plan.price_monthly_cents === 0) {
    return t("free")
  }
  const cents = getPlanRecurringAmountCents(
    plan,
    subscription.billing_interval,
    subscription.billing_interval_count,
  )
  const amount = `$${new Intl.NumberFormat(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false}).format(cents / 100)}`
  const suffix = formatIntervalSuffix(subscription.billing_interval, subscription.billing_interval_count, t)
  // Shopify surfaces the SAME amount as Stripe — only the wording differs (renews
  // via Shopify vs next charge), since billing is settled by Shopify, not a card.
  if (subscription.payment_provider === 'shopify') {
    return dateLabel ? t("shopifyDate", {amount, suffix, date: dateLabel}) : t("shopify", {amount, suffix})
  }
  return dateLabel ? t("nextCharge", {amount, suffix, date: dateLabel}) : `${amount}${suffix}`
}

/** A billing-period boundary as the Plan card shows it ("May 28"), in the viewer's time zone. */
export function formatPeriodDate(iso: string | null, timezone: string = 'UTC', locale = 'en'): string | null {
  return iso ? new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: timezone }) : null
}
